import { createImagesBucket, createImagesDns } from '@/environment';
import { useGetPresenterByUrl, useGetPresentersList } from '@/hooks/presenter';
import { useApplyBackgroundColor } from '@/hooks/useApplyBackgroundColor';
import { useGetAllVoices, useGetLanguageByVoiceID } from '@/pages/agents/hooks';
import { isTalkPresenter, loadSavedAgentState } from '@/pages/agents/utils';
import { getKnowledgeMappedDocuments, InitAgentState, InputAgent } from '@/state/agent';
import { useAgentEditorStore } from '@/state/root';
import { IAgent, Presenter, PresenterUsecase, TalkPresenter, VideoType } from '@d-id/types';
import { runInAction } from 'mobx';
import { nanoid } from 'nanoid';
import { useEffect } from 'react';
import { onApplyBackgroundSuccess } from '../pages/editor/components/AgentAppearance/utils';

export const useEditAgentInit = (agent: IAgent) => {
    const agentEditorStore = useAgentEditorStore();

    const presenter: Presenter | undefined = useGetPresenterByUrl(agent?.presenter?.thumbnail, PresenterUsecase.Agent);
    const voiceOptions = useGetAllVoices();
    const languageByVoiceID = useGetLanguageByVoiceID(agent?.presenter?.voice?.voice_id);
    const selectedLanguage = agent?.presenter?.voice?.language || languageByVoiceID;
    const selectedPresenterIsStitch = isTalkPresenter(agent.presenter) && agent.presenter?.stitch;
    const { data: presenters } = useGetPresentersList({ usecase: PresenterUsecase.Agent });
    const { mutateAsync: applyBackgroundColor } = useApplyBackgroundColor(onApplyBackgroundSuccess, agent?.presenter?.type as VideoType);

    const voicesLoaded = !!Object.keys(voiceOptions).length;

    useEffect(() => {
        runInAction(() => {
            let inputAgent: Partial<InputAgent> = {};
            if (agent?.preview_name) inputAgent.preview_name = agent.preview_name;
            agentEditorStore.setInputAgent(prev => ({ ...prev, ...inputAgent }));

            if (agent?.llm?.instructions) agentEditorStore.setLLM(llm => ({ ...llm, instructions: agent?.llm?.instructions }));
            agentEditorStore.setTriggers(agent?.triggers || {});

            const presenter_id = presenter?.id || (agent as any)?.presenter?.presenter_id;

            if (presenter_id) {
                agentEditorStore.setSelectedPresenter(presenter_id);
                agentEditorStore.setInitAgentState(prevState => ({ ...prevState, presenter_id }));
            }

            if ((agent?.presenter as TalkPresenter)?.source_url) {
                agentEditorStore.setPresenterBackgroundSourceUrl((agent?.presenter as TalkPresenter)?.source_url);
            }

            if (agent?.presenter?.background) {
                agentEditorStore.setPresenterBackgroundSourceUrl(agent?.presenter?.background?.source_url || '');
                agentEditorStore.setPresenterBackgroundColor(agent?.presenter?.background?.color || '');
                agentEditorStore.setPresenterBackgroundThumbnailUrl(agent?.presenter?.thumbnail || '');

                const originalPresenter = presenters?.find(presenter => presenter.id === presenter_id);
                if (agent.presenter.background.color && originalPresenter) {
                    const params = {
                        imageUrl: originalPresenter.thumbnail || '',
                        imageId: presenter_id,
                        backgroundColor: agent.presenter.background.color,
                        fileName: `merged-bg-color-${nanoid(8)}-image.png`,
                        bucket: createImagesBucket,
                        dns: createImagesDns,
                    };
                    applyBackgroundColor(params);
                }
            }
        });
    }, [presenter, agent]);

    useEffect(() => {
        if (selectedPresenterIsStitch !== undefined) agentEditorStore.setPresenterIsStitch(selectedPresenterIsStitch);
    }, [selectedPresenterIsStitch]);

    useEffect(() => {
        if (selectedLanguage) agentEditorStore.setTtsSelectedLanguage(selectedLanguage);
    }, [selectedLanguage]);

    useEffect(() => {
        if (agent.llm) agentEditorStore.setLLM(agent.llm);
    }, [agent?.llm]);

    useEffect(() => {
        agent?.id && agentEditorStore.setInputAgent(prev => ({ ...prev, id: agent.id! }));
    }, [agent?.id]);

    useEffect(() => {
        const update = async () => {
            if (!agentEditorStore.greetings?.length) {
                const agentGreetings = agent?.greetings || [];
                agentEditorStore.setGreetings(agentGreetings);
            }
            // @ts-ignore
            const initState: InitAgentState = {};
            try {
                if (
                    !!agent?.knowledge?.id &&
                    !agentEditorStore.uploadingDocumentFiles?.length &&
                    !agentEditorStore.deletedDocumentFiles?.length
                ) {
                    const docs = await getKnowledgeMappedDocuments(agent.knowledge.id);
                    agentEditorStore.setUploadingDocumentFiles(docs);
                    initState.documents = docs;
                    initState.knowledgeId = agent.knowledge.id;
                }
                if (!agentEditorStore.starterQuestions?.length) {
                    const starterMessage = agent.starter_message || agent.knowledge?.starter_message || [];
                    agentEditorStore.setStarterQuestions(starterMessage);
                }
                if (!agentEditorStore.baseKnowledge) {
                    const baseKnowledge = agent.llm?.prompt_customization?.prompt_knowledge ?? agent.knowledge?.base_knowledge ?? '';
                    agentEditorStore.setBaseKnowledge(baseKnowledge);
                }
            } catch (error) {
                console.error(error);
            }
            agentEditorStore.setInitAgentState(prevState => ({ ...prevState, ...initState }));
        };

        update();
    }, [agent]);

    useEffect(() => {
        if (!voicesLoaded) {
            return;
        }
        const loading = async () => {
            await loadSavedAgentState(agent, presenters, voiceOptions);
        };
        loading();
    }, [voicesLoaded]);
};
